import { Controller } from "@hotwired/stimulus";

// On click of a question card link:
// 1. preventDefault so the browser doesn't full-nav (Turbo Drive is globally off
//    in this app, so its document-level link interceptor isn't running).
// 2. Toggle the `is-selected` highlight on the clicked card immediately for
//    snappy feedback even before the right pane finishes loading.
// 3. Push the card's URL onto history so the address bar carries the
//    `question_id` param — this makes individual questions linkable (paste
//    into Slack/Teams) and lets Back navigate to the previously-selected
//    question. Filter-change paths still use replaceState/no-pushState; only
//    a card click (an explicit selection) creates a history entry.
// 4. Set the editor frame's src — Turbo's <turbo-frame> custom element
//    observes src changes and performs the fetch + swap itself (this works
//    regardless of session.drive).
export default class extends Controller {
  static targets = ["card"];

  connect() {
    this.boundPopstate = () => this.handlePopstate();
    window.addEventListener("popstate", this.boundPopstate);
  }

  disconnect() {
    window.removeEventListener("popstate", this.boundPopstate);
  }

  // When the user hits Back/Forward, the URL changes but the frame doesn't
  // reload by itself. Re-point the editor frame at the new URL so the pane
  // matches the address bar, and update the `is-selected` highlight.
  handlePopstate() {
    const frame = document.getElementById("qa_editor");
    if (!frame) return;

    const url = window.location.href;
    if (frame.src !== url) frame.src = url;

    const params = new URL(url).searchParams;
    const qid = params.get("question_id");
    this.cardTargets.forEach((c) => {
      c.classList.toggle("is-selected", String(c.dataset.questionId) === String(qid));
    });
  }

  select(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.currentTarget;
    if (!link || !link.href) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    // Highlight the card matching the link's `question_id`, regardless of
    // whether the click came from a left-pane card or a right-pane "Open
    // canonical" link inside the editor frame.
    const qid = new URL(link.href).searchParams.get("question_id");
    if (qid) {
      this.cardTargets.forEach((c) => {
        c.classList.toggle("is-selected", String(c.dataset.questionId) === String(qid));
      });
    }

    const frame = document.getElementById("qa_editor");
    if (!frame) {
      window.location.href = link.href;
      return;
    }

    if (link.href !== window.location.href) {
      window.history.pushState({ qaQuestion: true }, "", link.href);
    }

    frame.src = link.href;
  }
}
